/**
 * Progress-aware XHR upload shared by every browser upload path (guest trial
 * direct PUT + legacy multipart, member direct chunk PUT). Two guards ship
 * with it:
 *  - caller-supplied AbortSignal (cancel buttons);
 *  - a stall watchdog that aborts when NO upload bytes move for
 *    `stallTimeoutMs`, turning a dead connection into a retryable
 *    `XhrUploadStalledError` instead of an indefinite spinner.
 *
 * The watchdog only polices the request BODY: once every byte is out
 * (`loaded >= total`) it disarms, because from that point the clock measures
 * the server's work (ffprobe, storage writes), which legitimately exceeds
 * any byte-movement timeout.
 */

export const DEFAULT_UPLOAD_STALL_TIMEOUT_MS = 45_000

/** The stall watchdog aborted the transfer — no bytes moved for too long. */
export class XhrUploadStalledError extends Error {
  constructor() {
    super('Upload stalled: no data moved within the stall window.')
    this.name = 'XhrUploadStalledError'
  }
}

export interface XhrUploadOptions {
  onUploadProgress?: (fraction: number) => void
  /**
   * Every byte of the body is out and the request is now waiting on the
   * server. Fires ONCE, and only on a successful transmission (`upload.onload`,
   * not `loadend`, so an aborted or failed upload never reports "sent").
   *
   * It exists because "100%" and "done" are not the same moment: the server
   * still has to store the object, probe it and create rows, which on a large
   * file is the longest single wait in the whole flow. Without this signal the
   * UI sits on a full progress bar for a minute with nothing to say.
   */
  onBodySent?: () => void
  signal?: AbortSignal
  stallTimeoutMs?: number
}

export function sendXhrUpload(
  xhr: XMLHttpRequest,
  body: XMLHttpRequestBodyInit,
  opts: XhrUploadOptions & {
    resolve: (response: Response) => void
    reject: (reason: unknown) => void
  }
): void {
  const stallTimeoutMs = opts.stallTimeoutMs ?? DEFAULT_UPLOAD_STALL_TIMEOUT_MS
  let stallTimer: ReturnType<typeof setTimeout> | null = null
  let stalled = false
  let onExternalAbort: (() => void) | null = null

  const clearStallTimer = () => {
    if (stallTimer !== null) clearTimeout(stallTimer)
    stallTimer = null
  }
  const armStallTimer = () => {
    clearStallTimer()
    if (stallTimeoutMs <= 0) return
    stallTimer = setTimeout(() => {
      stalled = true
      xhr.abort()
    }, stallTimeoutMs)
  }
  const cleanup = () => {
    clearStallTimer()
    if (onExternalAbort && opts.signal) opts.signal.removeEventListener('abort', onExternalAbort)
  }

  xhr.onload = () => {
    cleanup()
    opts.resolve(responseFromXhr(xhr))
  }
  xhr.onerror = () => {
    cleanup()
    opts.reject(new TypeError('Network request failed'))
  }
  xhr.onabort = () => {
    cleanup()
    if (stalled) {
      opts.reject(new XhrUploadStalledError())
      return
    }
    opts.reject(new DOMException('Upload aborted', 'AbortError'))
  }

  if (opts.onBodySent) {
    // `upload.onload` fires only when the body went out in full; `loadend`
    // also fires for abort/error, which would announce a hand-off to a server
    // that never received the request.
    xhr.upload.onload = () => opts.onBodySent?.()
  }

  if (opts.onUploadProgress || stallTimeoutMs > 0) {
    xhr.upload.onprogress = (event) => {
      const bodyDone = event.lengthComputable && event.total > 0 && event.loaded >= event.total
      if (bodyDone) {
        // Body fully sent — from here the server is working, not the network.
        clearStallTimer()
      } else {
        armStallTimer()
      }
      if (!event.lengthComputable || event.total <= 0) return
      opts.onUploadProgress?.(Math.max(0, Math.min(1, event.loaded / event.total)))
    }
    // Fires when the body is out regardless of length computability — the
    // backstop disarm for uploads whose progress events carry no totals.
    xhr.upload.onloadend = () => clearStallTimer()
  }

  if (opts.signal) {
    if (opts.signal.aborted) {
      cleanup()
      opts.reject(new DOMException('Upload aborted', 'AbortError'))
      return
    }
    onExternalAbort = () => xhr.abort()
    opts.signal.addEventListener('abort', onExternalAbort, { once: true })
  }

  armStallTimer()
  xhr.send(body)
}

function headersFromXhr(rawHeaders: string): Headers {
  const headers = new Headers()
  for (const line of rawHeaders.trim().split(/[\r\n]+/)) {
    const separator = line.indexOf(':')
    if (separator <= 0) continue
    headers.append(line.slice(0, separator).trim(), line.slice(separator + 1).trim())
  }
  return headers
}

export function responseFromXhr(xhr: XMLHttpRequest): Response {
  return new Response(xhr.responseText, {
    status: xhr.status,
    statusText: xhr.statusText,
    headers: headersFromXhr(xhr.getAllResponseHeaders()),
  })
}
